import { Box, Flex, Input, Popover, Portal, Skeleton, Text, VStack } from '@chakra-ui/react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  FilterMenuItem,
} from '@/components/filters/filter-button';
import { Checkbox } from '@/components/ui/checkbox';
import { useDebounce } from '@/hooks/useDebounce';
import { useBrandDashboardAttributionCodes } from '@/hooks/queries/use-brand-dashboard-attribution-codes';

interface AttributionCodeFilterProps {
  paramName?: string;
}

export const AttributionCodeFilter: React.FC<AttributionCodeFilterProps> = ({
  paramName = 'dashboardAttributionCodes',
}) => {
  const { t } = useTranslation();
  const [urlCodes, setUrlCodes] = useQueryState(paramName, parseAsString);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);

  const selectedCodes = useMemo(
    () => (urlCodes ? urlCodes.split(',').filter(Boolean) : []),
    [urlCodes]
  );
  const [tempCodes, setTempCodes] = useState<string[]>(selectedCodes);

  const { data: options = [], isLoading } = useBrandDashboardAttributionCodes({
    search: debouncedSearch || undefined,
    limit: 100,
  });

  useEffect(() => {
    setTempCodes(selectedCodes);
  }, [selectedCodes]);

  const handleApply = useCallback(() => {
    setUrlCodes(tempCodes.length > 0 ? tempCodes.join(',') : null);
  }, [setUrlCodes, tempCodes]);

  const handleClear = useCallback(() => {
    setTempCodes([]);
    setUrlCodes(null);
  }, [setUrlCodes]);

  const handleClose = useCallback(() => {
    setTempCodes(selectedCodes);
    setSearch('');
  }, [selectedCodes]);

  const handleToggle = (code: string) => {
    setTempCodes((prevCodes) =>
      prevCodes.includes(code)
        ? prevCodes.filter((selectedCode) => selectedCode !== code)
        : [...prevCodes, code]
    );
  };

  const isFilterActive = selectedCodes.length > 0;

  const renderActiveOptions = () => (
    <Text as="span" lineClamp={1}>
      {selectedCodes.length === 1
        ? selectedCodes[0]
        : t('filters.attributionCode.selectedCount', { count: selectedCodes.length })}
    </Text>
  );

  return (
    <FilterButton isFilterActive={isFilterActive} onClearFilter={handleClear} onClose={handleClose}>
      <FilterButtonAction>
        <FilterButtonLabel>{t('filters.attributionCode.label')}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>{renderActiveOptions()}</FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply} maxWidth="320px">
            <FilterButtonMenuHeader
              title={t('filters.attributionCode.title')}
              onClearAll={handleClear}
            />
            <Box p={3}>
              <Input
                variant="outline"
                size="sm"
                mb={2}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('filters.attributionCode.search')}
              />
              {isLoading ? (
                <Box>
                  <Skeleton height="34px" mb={1} />
                  <Skeleton height="34px" mb={1} />
                  <Skeleton height="34px" />
                </Box>
              ) : (
                <VStack gap={0.5} align="stretch" maxHeight="300px" overflowY="auto">
                  {options.length === 0 && (
                    <Text fontSize="sm" color="dark" textAlign="center" py={2}>
                      {t('filters.attributionCode.noCodes')}
                    </Text>
                  )}
                  {options.map((option) => {
                    const selected = tempCodes.includes(option.code);
                    return (
                      <FilterMenuItem
                        key={`${option.code}-${option.affiliateId}-${option.programId}`}
                        onClick={() => handleToggle(option.code)}
                        isSelected={selected}
                      >
                        <Flex align="center" gap={2} minW={0} flex="1">
                          <Checkbox checked={selected} pointerEvents="none" aria-hidden size="sm" />
                          <VStack align="flex-start" gap={0} minW={0}>
                            <Text fontSize="sm" fontWeight="medium" lineClamp={1}>
                              {option.code}
                            </Text>
                            <Text fontSize="xs" color="fg.muted" lineClamp={1}>
                              {option.affiliateName} · {option.programName}
                            </Text>
                          </VStack>
                        </Flex>
                      </FilterMenuItem>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
